import React, { useEffect, useRef, useState } from "react";

/*
 * International (English) news ticker for the standard homepage.
 * Mirrors the Urdu ticker on the Pakistan homepage, but in English/LTR and
 * with international sources. Sources intentionally exclude CNN and BBC.
 */

const FALLBACK_HEADLINES = [
  "Select Proposal — trusted matrimonial matching for Pakistanis worldwide",
  "Verified profiles only — every member is checked before approval",
  "Register today and start your search",
  "Compatibility scores across 8 life areas — know before you talk",
  "Your privacy, your control — you decide who sees your photos",
  "Moving abroad? Find a match in your destination city",
  "Pakistani rishtas in Australia, Canada, UK, USA and the Gulf",
];

/*
 * English news RSS feeds. `weight` caps how many headlines we keep per feed.
 * No CNN / BBC by request (Sep 2026). Arab News and TRT World RSS both fail
 * through rss2json (HTTP 422), so they are deliberately not listed.
 */
const INTL_NEWS_FEEDS = [
  { url: "https://www.aljazeera.com/xml/rss/all.xml", weight: 6 }, // Al Jazeera
  { url: "https://www.dawn.com/feeds/home", weight: 5 }, // Dawn (Pakistan)
  { url: "https://www.theguardian.com/world/rss", weight: 4 }, // Guardian World
  { url: "https://www.middleeasteye.net/rss", weight: 3 }, // Middle East Eye
];

/* Light weather flavour for the biggest diaspora cities */
const WEATHER_CITIES = ["Sydney", "Toronto", "Dubai"];

async function fetchWeatherHeadlines() {
  try {
    const results = await Promise.allSettled(
      WEATHER_CITIES.map(async (city) => {
        const res = await fetch(`https://wttr.in/${city}?format=j1`, {
          signal: AbortSignal.timeout(5000),
        });
        const d = await res.json();
        const cur = d?.current_condition?.[0];
        if (!cur) return null;
        const desc = cur.weatherDesc?.[0]?.value || "";
        return `🌤️ ${city}: ${desc}, ${cur.temp_C}°C, humidity ${cur.humidity}%`;
      })
    );
    return results
      .filter((r) => r.status === "fulfilled" && r.value)
      .map((r) => r.value);
  } catch {
    return [];
  }
}

const TICKER_CACHE_KEY = "sp_intl_ticker_headlines";
const TICKER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const InternationalNewsTicker = () => {
  const [headlines, setHeadlines] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(TICKER_CACHE_KEY));
      if (
        cached &&
        cached.headlines?.length &&
        Date.now() - cached.ts < TICKER_CACHE_TTL
      ) {
        return cached.headlines;
      }
    } catch {
      /* ignore */
    }
    return FALLBACK_HEADLINES;
  });

  useEffect(() => {
    const fetchWithTimeout = (url, ms = 5000) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), ms);
      return fetch(url, { signal: controller.signal }).finally(() =>
        clearTimeout(timer)
      );
    };

    const fetchNews = async () => {
      const all = [];

      const feedPromises = INTL_NEWS_FEEDS.map(async ({ url: feedUrl, weight }) => {
        try {
          const res = await fetchWithTimeout(
            "https://api.rss2json.com/v1/api.json?rss_url=" +
              encodeURIComponent(feedUrl),
            5000
          );
          const data = await res.json();
          if (data.status === "ok" && data.items?.length) {
            const titles = data.items
              .map((item) => item.title?.replace(/<[^>]*>/g, "").trim())
              .filter((t) => t && t.length > 15);
            return titles.slice(0, weight);
          }
        } catch {
          /* skip failed feed */
        }
        return [];
      });

      const [feedResults, weatherLines] = await Promise.all([
        Promise.allSettled(feedPromises),
        fetchWeatherHeadlines(),
      ]);

      feedResults.forEach((r) => {
        if (r.status === "fulfilled" && r.value?.length) all.push(...r.value);
      });
      all.push(...weatherLines);

      const unique = [...new Set(all)];
      if (unique.length >= 3) {
        const shuffled = unique.sort(() => Math.random() - 0.5).slice(0, 25);
        setHeadlines(shuffled);
        try {
          localStorage.setItem(
            TICKER_CACHE_KEY,
            JSON.stringify({ headlines: shuffled, ts: Date.now() })
          );
        } catch {
          /* quota exceeded — ignore */
        }
      } else {
        setHeadlines([...FALLBACK_HEADLINES].sort(() => Math.random() - 0.5));
      }
    };

    fetchNews();
    const interval = setInterval(fetchNews, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Duplicate so the loop is seamless
  const tickerText = [...headlines, ...headlines];
  const trackRef = useRef(null);

  // JS-driven animation (same approach as the Urdu ticker — reliable on mobile
  // SPA navigation, no CSS @keyframes needed).
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    let offset = 0;
    const speed = 0.55; // px per frame (~33px/s at 60fps)

    const tick = () => {
      offset += speed;
      const halfWidth = el.scrollWidth / 2;
      if (offset >= halfWidth) offset -= halfWidth;
      el.style.transform = `translate3d(${-offset}px, 0, 0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [headlines]);

  return (
    <div
      className="bg-[#4a0000] text-white py-1 overflow-hidden"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div className="flex items-center">
        {/* Badge is deliberately smaller and tighter on phones — at the old
            size it ate a third of the bar and pushed the headlines off. */}
        {/* Badge font size is set inline with clamp() rather than a Tailwind
            responsive class: it must stay tiny on phones even if a stale CSS
            bundle is served, and clamp needs no breakpoint. */}
        <span
          className="bg-amber-400 text-[#4a0000] font-bold uppercase leading-none px-1 sm:px-2 py-[2px] sm:py-0.5 ml-1 mr-1.5 sm:mr-2 rounded flex-shrink-0 whitespace-nowrap"
          style={{ fontSize: "clamp(6px, 1.1vw, 10px)", letterSpacing: "0.02em" }}
        >
          World News
        </span>
        <div
          className="overflow-hidden flex-1 relative"
          style={{ minWidth: 0 }}
        >
          <div
            ref={trackRef}
            className="whitespace-nowrap text-[11px] sm:text-xs md:text-sm"
            style={{
              display: "inline-block",
              width: "max-content",
              willChange: "transform",
              backfaceVisibility: "hidden",
            }}
          >
            {tickerText.map((h, i) => (
              <span key={i} className="mx-3 sm:mx-4 md:mx-6">
                {h}
                <span
                  className="text-amber-400 mx-1.5 md:mx-2"
                  style={{ fontSize: "0.6em", verticalAlign: "middle" }}
                >
                  ◆
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternationalNewsTicker;
